import React, {
    useCallback, useState, useContext, useEffect
} from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/services';
import {
    ModalNextProvider
} from '@jutro/components';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { AddressLookupComponent } from 'tdic-capability-address-react';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import messages from './MailingAddressPage.messages';
import metadata from './MailingAddressPage.metadata.json5';
import commonMessages from '../../PolicyChangeCommon.messages';
import styles from '../../PolicyChange.module.scss';

function MailingAddressPage(props) {
    const translator = useContext(TranslatorContext);
    const {
        wizardData, updateWizardData, authHeader, user_name: userName, tncTransactionTypesSubTypes,
        email, termData: terms
    } = props;
    const { PolicyService } = useDependencies('PolicyService');
    const localeService = ServiceManager.getService('locale-service');
    const { submissionVM } = wizardData;
    const [tncCheck, updateTnCCheck] = useState({});
    const { EndorsementService } = useDependencies('EndorsementService');
    const {
        onValidate, initialValidation, isComponentValid, registerComponentValidation
    } = useValidation(
        'MailingAddressPage'
    );
    const [selectRadioYN, setSelectRadioYN] = useState();
    const [selectRadioContactUpdatedYN, setSelectRadioContactUpdatedYN] = useState();
    const [previousPrimaryNamedInsured, setPreviousPrimaryNamedInsured] = useState(null);
    const [isCompanyAccount, setCompanyAccount] = useState(false);
    const showAddressSuggestion = useCallback(async (address) => {
        const componentProps = {
            value: address
        };
        const results = await ModalNextProvider.showModal(
            <AddressLookupComponent {...componentProps} />
        ).catch(_.noop);
        return results;
    }, []);

    const validateAddress = useCallback(async () => {
        const addressResponse = await PolicyService.lookupMelissaAddress(
            {
                addressLine1: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.addressLine1', ''),
                addressLine2: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.addressLine2', ''),
                postalCode: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.postalCode', ''),
                state: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.state', ''),
                country: localeService.getDefaultCountryCode(),
                city: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.city', ''),
                county: _.get(submissionVM.value, 'baseData_Ext.newPolicyAddress.county', '')
            },
            authHeader
        );
        return addressResponse;
    }, [PolicyService, authHeader, localeService, submissionVM]);

    useEffect(() => {
        _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress',
            { country: localeService.getDefaultCountryCode() });
        _.set(submissionVM.value, 'baseData_Ext.isPPLocation', selectRadioYN);
        _.set(submissionVM.value, 'baseData_Ext.primaryPracticeAddress',
            { country: localeService.getDefaultCountryCode() });
        if (previousPrimaryNamedInsured === null) {
            const pniValue = _.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured', null);
            setPreviousPrimaryNamedInsured(_.cloneDeep(pniValue));
        }
        const firstName = _.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured.firstName', '');
        if (_.isEmpty(firstName)) {
            setCompanyAccount(true);
        }
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveEndorsement = useCallback(async () => {
        const requestPayload = _.omit(submissionVM.value, 'lobData');
        if (submissionVM.value.baseData_Ext.isPPLocation === true
            || _.isUndefined(submissionVM.value.baseData_Ext.isPPLocation)) {
            _.set(submissionVM.value, 'baseData_Ext.primaryPracticeAddress', undefined);
        }
        if (submissionVM.value.offeringCode !== 'PLClaimsMade_TDIC'
            && submissionVM.value.offeringCode !== 'PLOccurence_TDIC') {
            _.set(submissionVM.value, 'baseData_Ext.isPPLocation', undefined);
        }
        submissionVM.value = await EndorsementService.saveEndorsement(
            [requestPayload],
            authHeader
        );
        TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes, 'PolicyChange', 'mailingaddress', authHeader, _.get(submissionVM.value, 'jobID'), userName, email);
        return wizardData;
    }, [submissionVM, EndorsementService, authHeader, terms, tncTransactionTypesSubTypes,
        userName, email, wizardData]);

    const setAddressChange = useCallback((address) => {
        if (!_.isNil(address)) {
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.addressLine1', _.get(address, 'addressLine1', null));
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.addressLine2', _.get(address, 'addressLine2', ''));
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.country', localeService.getDefaultCountryCode());
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.city', _.get(address, 'city', null));
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.postalCode', _.get(address, 'postalCode', null));
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.state', _.get(address, 'state', null));
            _.set(submissionVM.value, 'baseData_Ext.newPolicyAddress.county', _.get(address, 'county', null));
            // updateWizardData(submissionVM);
        }
        return saveEndorsement(submissionVM.value);
    }, [submissionVM, updateWizardData, saveEndorsement, localeService]);

    const onNext = useCallback(async () => {
        const melissaResponse = await validateAddress();

        if (melissaResponse.matches.length === 0 && melissaResponse.standardizationStatus === 'Standardized') {
            return saveEndorsement(submissionVM.value);
        }

        const result = await showAddressSuggestion(melissaResponse);
        if (_.has(result, 'selectedAddress')) {
            const selectedValue = _.get(result, 'selectedAddress');
            const address = melissaResponse.matches[Number(selectedValue)];
            return setAddressChange(address);
        }
        if (_.has(result, 'isEdit') && result.isEdit) {
            return false;
        }
        if (_.has(result, 'cancel') && result.cancel) {
            return false;
        }
        if (_.has(result, 'keepAsEntered') && result.keepAsEntered) {
            return saveEndorsement(submissionVM.value);
        }
        return false;
    }, [validateAddress, showAddressSuggestion, submissionVM, setAddressChange, saveEndorsement]);

    const handleCheckboxValueChange = useCallback((value, path) => {
        const checkBoxValue = { ...tncCheck };
        _.set(checkBoxValue, path, value);
        updateTnCCheck(checkBoxValue);
    }, [tncCheck]);

    const isTnCAccepted = useCallback(() => {
        return _.get(tncCheck, 'addressConfirmation', false) && _.get(tncCheck, 'signatureConfirmation', false);
    }, [tncCheck]);

    const capitalizeFirstLetter = useCallback((word) => {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1);
    });
    const handleContact = useCallback((optionYesNo) => {
        setSelectRadioContactUpdatedYN(optionYesNo);
        _.set(submissionVM.value, 'baseData_Ext.isContactUpdated', optionYesNo);
        updateWizardData({ ...wizardData, submissionVM });
    }, [submissionVM, updateWizardData, wizardData]);

    const handleContactChange = useCallback((value, path) => {
        _.set(submissionVM.value, path, value);
        updateWizardData({ ...wizardData, submissionVM });
    }, [submissionVM, updateWizardData, wizardData]);

    const handleYesNo = useCallback((optionYesNo) => {
        _.set(submissionVM.value, 'baseData_Ext.isPPLocation', optionYesNo);
        setSelectRadioYN(optionYesNo);
        if (optionYesNo) {
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.country.value', localeService.getDefaultCountryCode());
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.addressLine1.value', '-');
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.city.value', '-');
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.state.value', 'IL');
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.county.value', '-');
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.postalCode.value', '60201');
        } else {
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.addressLine1.value', undefined);
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.city.value', undefined);
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.state.value', undefined);
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.county.value', undefined);
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.postalCode.value', undefined);
            _.set(submissionVM, 'baseData_Ext.primaryPracticeAddress.country.value', localeService.getDefaultCountryCode());
        }
    }, []);

    useEffect(() => {
        registerComponentValidation(isTnCAccepted);
    }, [registerComponentValidation, isTnCAccepted]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        addressCheckingBox: {
            showOptional: false,
            value: _.get(tncCheck, 'addressConfirmation', false),
            onValueChange: handleCheckboxValueChange,
            label: selectRadioYN === true || selectRadioYN === undefined
                ? translator(messages.acknowledgeMailingAddress)
                : translator(messages.noPrimaryPracticeAddress)
        },
        signatureCheckingBox: {
            showOptional: false,
            value: _.get(tncCheck, 'signatureConfirmation', false),
            onValueChange: handleCheckboxValueChange,
            label: messages.signatureMailingAddress
        },
        countyField: {
            value: _.get(submissionVM, 'baseData_Ext.newPolicyAddress.county.value')
        },
        yesNoRadioPrimaryPracticeLocationAddress: {
            value: selectRadioYN,
            visible: submissionVM.value.offeringCode === 'PLClaimsMade_TDIC' || submissionVM.value.offeringCode === 'PLOccurence_TDIC'
        },
        primaryPracticeFullAddressContainer: {
            visible: selectRadioYN === false
        },
        mobilePhoneD: {
            visible: !isCompanyAccount,
            value: previousPrimaryNamedInsured == null ? ' ' : previousPrimaryNamedInsured.mobilePhone,
        },
        workPhoneD: {
            label: isCompanyAccount ? messages.officePhone : messages.workPhone,
            value: (() => {
                if (!previousPrimaryNamedInsured) {
                    return ' ';
                }
                return previousPrimaryNamedInsured.workPhone;
            })(),
        },
        homePhoneD: {
            visible: !isCompanyAccount,
            value: previousPrimaryNamedInsured == null ? ' ' : previousPrimaryNamedInsured.homePhone,
        },
        primaryPhoneD: {
            visible: !isCompanyAccount,
            value: previousPrimaryNamedInsured == null ? ' ' : capitalizeFirstLetter(previousPrimaryNamedInsured.primaryPhoneType),
        },
        officePhoneD: {
            visible: isCompanyAccount,
            value: previousPrimaryNamedInsured == null ? ' ' : previousPrimaryNamedInsured.officePhone,
        },
        faxPhoneD: {
            value: previousPrimaryNamedInsured == null ? ' ' : previousPrimaryNamedInsured.faxPhone,
        },
        emailAddressD: {
            value: previousPrimaryNamedInsured == null ? ' ' : previousPrimaryNamedInsured.emailAddress,
        },
        yesNoRadioContactUpdate: {
            value: selectRadioContactUpdatedYN,
        },
        primaryPracticeContactDetailsContainer: {
            visible: selectRadioContactUpdatedYN === false
        },
        emailAddress: {
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.emailAddress')
        },
        primaryPhone: {
            visible: !isCompanyAccount,
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.primaryPhoneType')
        },
        workPhone: {
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.workPhone'),
            label: isCompanyAccount ? messages.officePhone : messages.workPhone,
        },
        mobilePhone: {
            visible: !isCompanyAccount,
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.mobilePhone')
        },
        homePhone: {
            visible: !isCompanyAccount,
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.homePhone')
        },
        faxPhone: {
            onValueChange: (value) => handleContactChange(value, 'baseData_Ext.primaryNamedInsured.faxPhone')
        }
    };

    const resolvers = {
        resolveComponentMap: {
            addressLookupComponent: AddressLookupComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handleContact: handleContact,
            handleYesNo: handleYesNo
        }
    };

    return (
        <WizardPage
            onNext={onNext}
            nextLabel={translator(messages.submitAddress)}
            cancelLabel={commonMessages.cancelAllChanges}
            previousLabel={translator(messages.back)}
            disableNext={!isComponentValid || !isTnCAccepted}
            skipWhen={initialValidation}
            showPrevious
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

MailingAddressPage.propTypes = wizardProps;
export default withAuthenticationContext(withApplicationPropertyContext(MailingAddressPage));
